import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, Alert, CircularProgress,
  Paper, Stack, IconButton, Tooltip, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText
} from '@mui/material';
import { CloudUpload, Visibility, VisibilityOff, Description, FolderOpen, InsertDriveFile } from '@mui/icons-material';
import { contractService, documentService } from '../services/api';

/**
 * ContractUploadForPerson - Extracts contract data via AI and provides inline preview.
 * Now supports selecting files from blob storage OR uploading from local computer.
 *
 * Props:
 *   personId - the Person ID (used for validation message)
 *   onExtracted - callback({ extractedData, file }) after successful AI extraction
 *   existingBlobPath - if contract already has a file, show View button
 *   disabled - disable upload
 *   previewHeight - height of inline preview iframe (default: 400)
 */
const ContractUploadForPerson = ({
  personId,
  onExtracted,
  existingBlobPath = null,
  disabled = false,
  previewHeight = 400
}) => {
  const [file, setFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [localPreviewUrl, setLocalPreviewUrl] = useState('');
  const [blobPreviewUrl, setBlobPreviewUrl] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [loadingExistingPreview, setLoadingExistingPreview] = useState(false);

  // Blob file picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [blobFiles, setBlobFiles] = useState([]);
  const [loadingBlobFiles, setLoadingBlobFiles] = useState(false);
  const [pickerError, setPickerError] = useState('');

  // Load existing blob preview on mount if available
  useEffect(() => {
    if (existingBlobPath && !blobPreviewUrl && !file) {
      loadExistingPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingBlobPath]);

  const loadExistingPreview = async () => {
    if (!existingBlobPath) return;
    setLoadingExistingPreview(true);
    try {
      const response = await contractService.getFilePreview(existingBlobPath);
      if (response.sasUrl) {
        setBlobPreviewUrl(response.sasUrl);
      }
    } catch (err) {
      console.error('Error loading existing preview:', err);
    } finally {
      setLoadingExistingPreview(false);
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setSelectedFileName(selectedFile.name);
      setError('');
      setSuccess(false);
      setBlobPreviewUrl('');
      // Create local preview URL
      const blobUrl = URL.createObjectURL(selectedFile);
      setLocalPreviewUrl(blobUrl);
      setShowPreview(true); // Auto-show preview when file selected
    }
  };

  // Open blob file picker
  const handleOpenPicker = async () => {
    setPickerOpen(true);
    setPickerError('');
    setLoadingBlobFiles(true);
    try {
      const response = await documentService.browsePersonFiles(personId);
      setBlobFiles(response.files || []);
    } catch (err) {
      console.error('Error loading blob files:', err);
      setPickerError('Could not load files from storage');
      setBlobFiles([]);
    } finally {
      setLoadingBlobFiles(false);
    }
  };

  // Handle selecting a file from blob storage
  const handleSelectBlobFile = async (blobFile) => {
    setPickerOpen(false);
    setError('');
    setSuccess(false);

    try {
      // Download the file through backend proxy (avoids CORS issues)
      const blob = await documentService.downloadBlobFile(blobFile.blobPath);

      // Create a File object from the blob
      const fileName = blobFile.fileName;
      const fileObj = new File([blob], fileName, { type: blob.type || blobFile.contentType || 'application/pdf' });

      // Create local preview URL from the downloaded blob
      const previewUrl = URL.createObjectURL(blob);

      setFile(fileObj);
      setSelectedFileName(fileName);
      setLocalPreviewUrl(previewUrl);
      setBlobPreviewUrl('');
      setShowPreview(true);
    } catch (err) {
      console.error('Error loading file from storage:', err);
      setError('Could not load selected file');
    }
  };

  // Only extract data with AI - no upload yet (upload happens on Save)
  const handleExtract = async () => {
    if (!file) return;

    setExtracting(true);
    setError('');

    try {
      // Extract data with AI (file stays in browser memory)
      const extractedData = await contractService.extractFromFile(file);

      setSuccess(true);

      // Notify parent with extracted data AND the file (for later upload on Save)
      onExtracted?.({
        extractedData,
        file  // Pass file to parent for upload on Save
      });

    } catch (err) {
      console.error('Extract error:', err);
      setError(err.response?.data || err.message || 'Error extracting contract data');
    } finally {
      setExtracting(false);
    }
  };

  const handleTogglePreview = () => {
    if (!showPreview && existingBlobPath && !blobPreviewUrl) {
      loadExistingPreview();
    }
    setShowPreview(!showPreview);
  };

  const handleViewInNewTab = async () => {
    const urlToOpen = blobPreviewUrl || localPreviewUrl;
    if (urlToOpen) {
      window.open(urlToOpen, '_blank');
    } else if (existingBlobPath) {
      try {
        const response = await contractService.getFilePreview(existingBlobPath);
        if (response.sasUrl) {
          window.open(response.sasUrl, '_blank');
        }
      } catch (err) {
        console.error('Error getting file preview:', err);
        setError('Could not load contract file');
      }
    }
  };

  const previewUrl = blobPreviewUrl || localPreviewUrl;
  const hasPreviewAvailable = previewUrl || existingBlobPath;

  return (
    <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          <Description sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
          Contract Document
          {existingBlobPath && !file && (
            <Typography component="span" variant="caption" color="success.main" sx={{ ml: 1 }}>
              ✓ Uploaded
            </Typography>
          )}
        </Typography>

        <Stack direction="row" spacing={0.5}>
          {hasPreviewAvailable && (
            <>
              <Tooltip title={showPreview ? "Hide preview" : "Show preview"}>
                <IconButton size="small" onClick={handleTogglePreview} color="primary">
                  {showPreview ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Open in new tab">
                <IconButton size="small" onClick={handleViewInNewTab} color="default">
                  <Visibility fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        {/* Primary: Select from Storage */}
        <Button
          variant="contained"
          size="small"
          startIcon={<FolderOpen />}
          onClick={handleOpenPicker}
          disabled={disabled || !personId}
        >
          Select from Storage
        </Button>

        {/* Secondary: Upload from local computer */}
        <input
          accept="image/*,.pdf"
          style={{ display: 'none' }}
          id="contract-person-file-input"
          type="file"
          onChange={handleFileChange}
          disabled={disabled}
        />
        <label htmlFor="contract-person-file-input">
          <Button
            variant="outlined"
            component="span"
            size="small"
            startIcon={<CloudUpload />}
            disabled={disabled}
          >
            Upload from PC
          </Button>
        </label>

        {file && (
          <>
            <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedFileName || file.name}
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={handleExtract}
              disabled={extracting || disabled}
              startIcon={extracting && <CircularProgress size={16} />}
            >
              {extracting ? 'Extracting...' : 'Extract Data'}
            </Button>
          </>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mt: 1 }}>Data extracted! File will be uploaded when you save.</Alert>}

      {/* Inline Preview */}
      <Collapse in={showPreview && (previewUrl || loadingExistingPreview)}>
        <Box sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
          {loadingExistingPreview ? (
            <Box sx={{ height: previewHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                Loading preview...
              </Typography>
            </Box>
          ) : previewUrl ? (
            <iframe
              title="contract-preview"
              src={previewUrl}
              style={{ width: '100%', height: previewHeight, border: 0 }}
            />
          ) : null}
        </Box>
      </Collapse>

      {/* Blob File Picker Dialog */}
      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <FolderOpen sx={{ mr: 1, verticalAlign: 'middle' }} />
          Select Contract from Storage
        </DialogTitle>
        <DialogContent dividers>
          {loadingBlobFiles ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : pickerError ? (
            <Alert severity="error">{pickerError}</Alert>
          ) : blobFiles.length === 0 ? (
            <Alert severity="info">No files found in storage for this person. Upload documents first.</Alert>
          ) : (
            <List dense>
              {blobFiles.map((bf, idx) => (
                <ListItem key={bf.id || idx} disablePadding>
                  <ListItemButton onClick={() => handleSelectBlobFile(bf)}>
                    <ListItemIcon>
                      <InsertDriveFile color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={bf.fileName}
                      secondary={
                        <>
                          {bf.documentType && <strong>{bf.documentType} • </strong>}
                          {bf.sizeFormatted || `${(bf.size / 1024).toFixed(1)} KB`}
                          {bf.isRegistered && ' • ✓'}
                        </>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ContractUploadForPerson;

